"use client";

import { useQuery } from "@tanstack/react-query";
import { Button } from "design-system/button";
import { Dialog, DialogContent, DialogDescription } from "design-system/dialog";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { getTurnkeyRecoverySessionProfileId } from "@/config/turnkey";
import { getV3TurnkeySuborgId } from "@/gen/orval/webplatform-v3/endpoints/embedded-wallets/embedded-wallets.gen";
import { useCanCreateEmbeddedWallet } from "@/lib/hooks/flags/use-can-create-embedded-wallet";
import type { UseDialogReturn } from "@/lib/hooks/use-dialog";
import { useUser } from "@/lib/integrations/descope";
import { localStorageHelpers } from "@/lib/utils/storage-apis";
import { useJwt } from "@/subsystems/access/json-web-token/hooks/use-jwt";
import { AuthErrorView } from "@/subsystems/access/turnkey/components/views/auth-error-view";
import { LoadingTurnkeyView } from "@/subsystems/access/turnkey/components/views/loading-turnkey-view";
import { StepHeader } from "@/subsystems/access/turnkey/components/views/step-header";
import { useRecoveryStatus } from "@/subsystems/embedded-wallets/use-recovery-status";
import { useNeedsSignerEnroll } from "./use-needs-signer-enroll";

// The flow pulls in the Turnkey SDK; load it only when the dialog first opens, like the auth dialog.
const SignerEnrollFlow = dynamic(() => import("./signer-enroll-flow").then((mod) => mod.SignerEnrollFlow), {
  loading: () => <LoadingTurnkeyView />,
  ssr: false,
});

// Same SDK weight; only pulled in when the signer accepts the SMS-recovery offer after enrolling.
const SmsRecoverySetupFlow = dynamic(
  () =>
    import("@/subsystems/embedded-wallets/components/sms-recovery/sms-recovery-setup-flow").then(
      (mod) => mod.SmsRecoverySetupFlow,
    ),
  { loading: () => <LoadingTurnkeyView />, ssr: false },
);

// Bumped to re-surface the one-time prompt to everyone if the enroll flow materially changes.
const AUTO_PROMPT_VERSION = "v1";

function seenKey(orgId: string, userId: string) {
  return `__agora_signer_enroll_auto_prompt_seen.${AUTO_PROMPT_VERSION}.${orgId}.${userId}`;
}

// First time a user enters app as an invited signer without a passkey, open dialog automatically.
export function useSignerEnrollAutoPrompt(dialogControls: UseDialogReturn<void>) {
  const jwt = useJwt();
  const orgId = jwt?.organization?.pk;
  const userId = jwt?.user?.pk;
  const prompted = useRef(false);
  const needsEnroll = useNeedsSignerEnroll();

  useEffect(() => {
    if (prompted.current || !needsEnroll || !orgId || !userId) return;
    const key = seenKey(orgId, userId);
    if (localStorageHelpers.get(key) === "1") return;
    prompted.current = true;
    // Marked seen on open, not on enroll: a dismissed prompt falls back to the always-present banner,
    // so the modal never re-interrupts an unrelated session.
    localStorageHelpers.set(key, "1");
    dialogControls.trigger();
  }, [needsEnroll, orgId, userId, dialogControls]);
}

// Dialog to complete passkey setup for new signer
export function SignerEnrollDialog({ dialogControls }: { dialogControls: UseDialogReturn<void> }) {
  const enabled = useCanCreateEmbeddedWallet();
  useSignerEnrollAutoPrompt(dialogControls);
  // Never unmount an open dialog on a transient flag read; see CreateEmbeddedWalletDialog.
  if (!enabled && !dialogControls.props.open) return null;

  return (
    <Dialog {...dialogControls.props}>
      {/* Opens on its own at sign-in, so the default focus on the close button would show a focus ring unprompted. */}
      <DialogContent className="sm:max-w-xl" onOpenAutoFocus={(event) => event.preventDefault()}>
        {dialogControls.props.open ? <SignerEnrollBody onClose={dialogControls.dismiss} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function SignerEnrollBody({ onClose }: { onClose: () => void }) {
  const { user } = useUser();
  const email = user?.email ?? "";
  const sessionProfileId = getTurnkeyRecoverySessionProfileId();
  const [phase, setPhase] = useState<"sms-suggest" | "setup" | "sms">("setup");
  // Withdrawing an invitation deletes the invitee's Turnkey user, so a fresh status read says "not a signer".
  // Only checked before enrollment starts: once a passkey is registered the caller is a signer again.
  const status = useRecoveryStatus();
  const withdrawn = phase === "setup" && status.data?.hasWallet === true && status.data.isSigner === false;
  const lookup = useQuery({
    enabled: Boolean(email),
    queryFn: () => getV3TurnkeySuborgId(),
    queryKey: ["turnkey-signer-enroll-suborg-lookup", email],
    staleTime: 30_000,
  });

  // Enrollment ends with a passkey login, so the session is full-access by now and the SMS flow's re-auth step
  // auto-advances to the phone entry without a second prompt.
  if (phase === "sms") {
    return <SmsRecoverySetupFlow entry="signer-enroll" mode="enable" onClose={onClose} />;
  }

  if (phase === "sms-suggest") {
    return (
      <>
        <StepHeader title="You're a signer" />
        <DialogDescription asChild className="text-neutral-600" data-testid="signer-enroll-done">
          <div>
            <p>You can now approve transactions on this wallet. One more step is recommended.</p>
            <p className="mt-2">Add SMS recovery, so your passkey can&apos;t be reset from your email alone.</p>
          </div>
        </DialogDescription>
        <div className="flex justify-end gap-2">
          <Button data-testid="close-signer-enroll-btn" onClick={onClose} type="button" variant="outline">
            Skip for now
          </Button>
          <Button data-testid="signer-enroll-add-sms-btn" onClick={() => setPhase("sms")} type="button">
            Add SMS recovery
          </Button>
        </div>
      </>
    );
  }

  if (withdrawn) {
    return (
      <AuthErrorView
        actions={
          <Button className="w-full" data-testid="close-signer-enroll-btn" onClick={onClose} type="button">
            Close
          </Button>
        }
        description="It was withdrawn, so there's nothing to enroll in. If you should be a signer, ask a signer on your team to invite you again."
        testId="signer-enroll-withdrawn"
        title="This invitation is no longer open"
      />
    );
  }

  if (!email || !sessionProfileId || lookup.isError || (lookup.data && !lookup.data.subOrgId)) {
    return (
      <AuthErrorView
        actions={
          <Button className="w-full" data-testid="close-signer-enroll-btn" onClick={onClose} type="button">
            Close
          </Button>
        }
        description="We couldn't start wallet setup. Refresh the page and try again, or contact support if it keeps happening."
        testId="signer-enroll-error"
        title="Couldn't start setup"
      />
    );
  }

  if (!lookup.data?.subOrgId) return <LoadingTurnkeyView />;

  return (
    <SignerEnrollFlow
      email={email}
      onBack={onClose}
      onCompleted={() => setPhase("sms-suggest")}
      sessionProfileId={sessionProfileId}
      subOrgId={lookup.data.subOrgId}
    />
  );
}
